import React from 'react';
import { Box, SxProps } from '@mui/material';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';
import { typographyTokens } from '../../theme/typography-tokens';

export interface ChipFieldProps
  extends
    Omit<React.HTMLAttributes<HTMLSpanElement>, keyof PounceStyleProps | 'sx'>,
    PounceStyleProps {
  /**
   * Field-name text content. The colon (or any separator) is **not** added
   * automatically — type it literally if needed.
   */
  children: React.ReactNode;
  /** Additional styles to apply (combined with pounce props). */
  sx?: SxProps;
}

/**
 * Cyan field-prefix span for `InteractiveChip`.
 *
 * Renders the leading field name in the chip's accent color (cyan-leaning
 * interactive field token, resolved via `--chip-interactive-field-color`).
 * Any trailing separator (colon, dash, etc.) must be included in `children`
 * verbatim — the primitive does not add one. Accepts pounceV2 style props
 * (`mr`, `mt`, `width`, …) and the standard `sx` escape hatch.
 *
 * @example
 * ```tsx
 * <InteractiveChip variant="primary" onClick={openFilter}>
 *   <ChipField>Severity:</ChipField>
 *   <ChipLabel>Critical</ChipLabel>
 * </InteractiveChip>
 * ```
 */
export const ChipField = React.forwardRef<HTMLSpanElement, ChipFieldProps>(function ChipField(
  { children, sx, ...rest },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);
  return (
    <Box
      component="span"
      ref={ref}
      sx={
        {
          color: 'var(--chip-interactive-field-color, currentColor)',
          fontFamily: typographyTokens.fontFamilies.sans,
          fontWeight: typographyTokens.fontWeights.medium,
          ...pounceStyles,
          ...sx,
        } as SxProps
      }
      {...otherProps}
    >
      {children}
    </Box>
  );
});
